import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	MenuList,
	MenuListDivider,
	ListItem,
	Input
} from '@clickguard/design-system'
import { AdAccountDTO } from 'api-models'
import { useAccountStore, useAssetStore } from 'store'
import { useStores } from 'hooks'
import { useShallow } from 'zustand/react/shallow'
import { PlatformIcon } from 'components/platform-icon'
import Dataflow02 from '@untitled-ui/icons-react/build/esm/Dataflow02'
import Plus from '@untitled-ui/icons-react/build/esm/Plus'
import { adAccountLabel } from 'utils/ad-account-label'
import { BreadcrumbTrigger } from './breadcrumb-trigger'
import { Radio } from './radio'

type Platform = 'GADS' | 'FADS' | 'BADS'

const PLATFORM_LABELS: Record<Platform, string> = {
	GADS: 'Google Ads',
	FADS: 'Meta Ads',
	BADS: 'Microsoft Ads'
}

interface AdAccountsSelectorSingleProps {
	adAccounts: AdAccountDTO[]
	selectedAdAccount: AdAccountDTO | null
	onSelect: (account: AdAccountDTO) => void
	/** Accounts matching this predicate are shown but not selectable (e.g. non-GAds on Refunds Agent). */
	disabledPredicate?: (account: AdAccountDTO) => boolean
}

export const AdAccountsSelectorSingle = ({
	adAccounts,
	selectedAdAccount,
	onSelect,
	disabledPredicate
}: AdAccountsSelectorSingleProps) => {
	const { accountStore, userStore } = useStores()
	const { user } = userStore
	const navigate = useNavigate()

	const { asset } = useAssetStore(useShallow((s) => ({ asset: s.asset })))
	const { account } = useAccountStore(useShallow((s) => ({ account: s.account })))

	const [isOpen, setIsOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')
	const [activePlatforms, setActivePlatforms] = useState<Platform[]>([])
	const containerRef = useRef<HTMLDivElement>(null)

	const userAccess = accountStore.userAccessLevel(user?.id || '')
	const isManagerOrOwner = userAccess === 'OWNER' || userAccess === 'WRITE'

	const platformCounts = useMemo(() => {
		const counts: Record<Platform, number> = { GADS: 0, FADS: 0, BADS: 0 }
		adAccounts.forEach((acc) => {
			counts[acc.platform] = (counts[acc.platform] || 0) + 1
		})
		return counts
	}, [adAccounts])

	const allPlatforms: Platform[] = ['GADS', 'FADS', 'BADS']

	const visibleAccounts = useMemo(() => {
		let filtered = adAccounts
		if (activePlatforms.length > 0) {
			filtered = filtered.filter((acc) =>
				activePlatforms.includes(acc.platform)
			)
		}
		if (searchQuery) {
			filtered = filtered.filter((acc) =>
				adAccountLabel(acc).toLowerCase().includes(searchQuery.toLowerCase())
			)
		}
		return filtered
	}, [adAccounts, activePlatforms, searchQuery])

	useEffect(() => {
		if (!isOpen) return
		const handleClickOutside = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setIsOpen(false)
				setSearchQuery('')
			}
		}
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				setIsOpen(false)
				setSearchQuery('')
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		document.addEventListener('keydown', handleEscape)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('keydown', handleEscape)
		}
	}, [isOpen])

	const handleOpen = () => {
		setActivePlatforms([])
		setSearchQuery('')
		setIsOpen(true)
	}

	const handlePickAccount = (acc: AdAccountDTO) => {
		if (disabledPredicate?.(acc)) return
		onSelect(acc)
		setIsOpen(false)
		setSearchQuery('')
	}

	const togglePlatform = (platform: Platform) => {
		setActivePlatforms((prev) =>
			prev.includes(platform)
				? prev.filter((p) => p !== platform)
				: [...prev, platform]
		)
	}

	const handleConnectAdAccount = () => {
		const accountId = account?.id
		if (accountId && asset?.id) {
			navigate(`/workspace/${accountId}/asset/${asset.id}/ppc-accounts`)
		}
		setIsOpen(false)
	}

	const label = selectedAdAccount
		? adAccountLabel(selectedAdAccount)
		: 'Select ad account'

	return (
		<div className="relative flex min-w-0" ref={containerRef}>
			<BreadcrumbTrigger
				icon={
					selectedAdAccount ? (
						<PlatformIcon
							platform={selectedAdAccount.platform}
							className="w-4 h-4"
						/>
					) : (
						<Dataflow02 className="size-4 text-[var(--text-secondary,#535255)]" />
					)
				}
				label={label}
				onClick={() => {
					if (adAccounts.length === 0) return
					isOpen ? setIsOpen(false) : handleOpen()
				}}
				isOpen={isOpen}
			/>

			{isOpen && (
				<div className="absolute top-full left-0 mt-1 z-50">
					<MenuList className="w-[430px] !p-[10px]" role="listbox">
						<div className="flex items-center gap-[4px] px-[4px] leading-[16px]">
							<span className="font-['Host_Grotesk',sans-serif] text-[12px] leading-[16px] text-[var(--text-secondary,#535255)] whitespace-nowrap">
								Ad accounts
							</span>
						</div>

						<div className="flex gap-[10px] mt-[10px]">
							{allPlatforms.map((platform) => {
								const count = platformCounts[platform]
								const hasAccounts = count > 0
								const isSelected =
									activePlatforms.length > 0 &&
									activePlatforms.includes(platform)
								return (
									<button
										key={platform}
										type="button"
										onClick={() =>
											hasAccounts && togglePlatform(platform)
										}
										className={`
											relative w-auto min-w-[130px] h-[70px] flex flex-col items-center justify-center gap-[4px] px-[6px] py-[8px] rounded-[12px] border overflow-hidden transition-colors
											${
												!hasAccounts
													? 'border-[var(--border-default,#e1e1e1)] bg-[var(--bg-secondary,#f9f9f9)] opacity-40 cursor-not-allowed'
													: isSelected
														? 'border-[var(--border-brand-medium,#7169ff)] bg-[var(--lavender-fill-soft,rgba(238,237,254,0.5))]'
														: 'border-[var(--border-default,#e1e1e1)] bg-[rgb(var(--cg-surface))] hover:bg-[var(--bg-hover,#f9f9f9)] cursor-pointer'
											}
										`}
									>
										{isSelected && (
											<div className="absolute top-[9px] right-[9px] w-[9.333px] h-[9.333px] rounded-[2.333px] bg-[#554bfd] border-[0.583px] border-solid border-[#554bfd] flex items-center justify-center overflow-hidden">
												<svg
													width="7"
													height="7"
													viewBox="0 0 12 12"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														d="M10 3L4.5 8.5L2 6"
														stroke="white"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
											</div>
										)}
										<PlatformIcon
											platform={platform}
											className="w-[22px] h-[22px]"
										/>
										<span className="font-['Host_Grotesk',sans-serif] text-[12px] leading-[16px] whitespace-nowrap text-[var(--text-link,#554bfd)]">
											{hasAccounts
												? PLATFORM_LABELS[platform]
												: 'No accounts connected'}
										</span>
									</button>
								)
							})}
						</div>

						<div className="mt-[10px]">
							<Input
								size="sm"
								state="default"
								placeholder="Search ad accounts..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
							/>
						</div>

						<div className="mt-[4px] max-h-[200px] overflow-y-auto">
							{visibleAccounts.length > 0 ? (
								visibleAccounts.map((acc) => {
									const isChecked = selectedAdAccount?.id === acc.id
									const isDisabled = disabledPredicate?.(acc) ?? false
									return (
										<ListItem
											key={acc.id}
											label={adAccountLabel(acc)}
											disabled={isDisabled}
											leadingControl={
												<div onClick={(e) => e.stopPropagation()}>
													<Radio checked={isChecked} />
												</div>
											}
											leadingIcon={
												<PlatformIcon
													platform={acc.platform}
													className="w-4 h-4"
												/>
											}
											onClick={() => handlePickAccount(acc)}
										/>
									)
								})
							) : (
								<div className="px-[8px] py-[8px]">
									<span className="font-['Host_Grotesk',sans-serif] text-[14px] leading-[18px] text-[var(--text-tertiary,#9f9ea3)]">
										No ad accounts found
									</span>
								</div>
							)}
						</div>

						{isManagerOrOwner && (
							<>
								<MenuListDivider />
								<ListItem
									label="Connect new ad account"
									leadingIcon={<Plus className="size-5" />}
									onClick={handleConnectAdAccount}
								/>
							</>
						)}
					</MenuList>
				</div>
			)}
		</div>
	)
}
